import { resolveGrowthEngineCtaPath } from './growthEngineCtaRoutes'

/**
 * Phase 4 — Ollie's once-only moments on the opening: the "Meet the new
 * Ollie" card (once per person) and the weekly brief (first business-day
 * open of the week). Express decides and claims them; this only shapes them
 * for the panel. Every line is Express copy built from the evidence.
 */

export const OLLIE_MOMENT_KINDS = Object.freeze({
  FIRST_USE_INTRO: 'first_use_intro',
  MEET_CARD: 'meet_card',
  WEEKLY_BRIEF: 'weekly_brief',
})

export const OLLIE_MOMENT_COPY = Object.freeze({
  dismiss: 'Got it',
  emailStephen: 'Email Stephen',
  briefAsk: 'Ask Ollie about this',
})

const text = value => {
  if (value == null) return null
  const trimmed = String(value).trim()
  return trimmed || null
}

const lines = value =>
  Array.isArray(value) ? value.map(text).filter(Boolean) : []

const todayJobButton = (todayJob, venueId) => {
  const cta = todayJob?.cta
  const path =
    cta?.action && text(cta.label)
      ? resolveGrowthEngineCtaPath(venueId, cta.action)
      : null
  if (path) return { label: text(cta.label), path }
  // This plan can't do the job here (Free stock takes on desktop).
  const note = text(todayJob?.honestNote)
  return note ? { label: null, path: null, note } : null
}

/**
 * The moment's finding, for "Ask Ollie about this". Its action button stays
 * on the finding card below, so the same job never has two buttons.
 */
const findingPart = (finding, askQuestion) => {
  if (!finding?.checkId) return { checkId: null, ask: null }
  return {
    checkId: String(finding.checkId),
    ask: text(askQuestion) || text(finding.askOllie?.question),
  }
}

/** The feedback invitation, with the address as a link rather than bare text. */
const feedbackPart = feedback => {
  const invitation = text(feedback?.text)
  const mailto = text(feedback?.mailto)
  if (!invitation) return null
  const email = text(feedback?.email)
  const lead =
    email && invitation.endsWith(email)
      ? invitation.slice(0, -email.length).replace(/[\s:]+$/, '')
      : invitation
  return { text: lead, email, mailto }
}

export function buildOllieMeetCardDisplay(meetCard, venueId) {
  if (!text(meetCard?.heading)) return null
  const finding = findingPart(meetCard.finding, meetCard.ask?.question)
  return {
    kind: OLLIE_MOMENT_KINDS.MEET_CARD,
    heading: text(meetCard.heading),
    intro: text(meetCard.intro),
    lines: lines(meetCard.noticed),
    ...finding,
    askLabel: finding.ask
      ? text(meetCard.ask?.label) || OLLIE_MOMENT_COPY.briefAsk
      : null,
    todayJob: todayJobButton(meetCard.todayJob, venueId),
    feedback: feedbackPart(meetCard.feedback),
  }
}

export function buildOllieBriefDisplay(brief, venueId) {
  if (!text(brief?.heading) || !lines(brief.lines).length) return null
  const finding = findingPart(brief.finding)
  return {
    kind: OLLIE_MOMENT_KINDS.WEEKLY_BRIEF,
    heading: text(brief.heading),
    intro: null,
    lines: lines(brief.lines),
    ...finding,
    askLabel: finding.ask ? OLLIE_MOMENT_COPY.briefAsk : null,
    todayJob: todayJobButton(brief.todayJob, venueId),
    feedback: null,
  }
}

export function buildOllieFirstUseIntroDisplay(firstUseIntro) {
  if (!text(firstUseIntro?.heading)) return null
  const suggestions = Array.isArray(firstUseIntro.suggestions)
    ? firstUseIntro.suggestions
        .map(item => ({
          id: text(item?.id),
          text: text(item?.text),
        }))
        .filter(item => item.id && item.text)
        .slice(0, 3)
    : []
  return {
    kind: OLLIE_MOMENT_KINDS.FIRST_USE_INTRO,
    heading: text(firstUseIntro.heading),
    intro: text(firstUseIntro.intro),
    lines: [],
    checkId: null,
    ask: null,
    askLabel: null,
    todayJob: null,
    feedback: null,
    suggestions,
    dismissLabel: text(firstUseIntro.dismissLabel) || OLLIE_MOMENT_COPY.dismiss,
  }
}

/** Whichever moment Express claimed for this open, or null. */
export function buildOllieMomentDisplay(opening, venueId) {
  if (!opening?.moment) return null
  if (opening.moment === OLLIE_MOMENT_KINDS.FIRST_USE_INTRO) {
    return buildOllieFirstUseIntroDisplay(opening.firstUseIntro)
  }
  if (opening.moment === OLLIE_MOMENT_KINDS.MEET_CARD) {
    return buildOllieMeetCardDisplay(opening.meetCard, venueId)
  }
  if (opening.moment === OLLIE_MOMENT_KINDS.WEEKLY_BRIEF) {
    return buildOllieBriefDisplay(opening.brief, venueId)
  }
  return null
}
